<template>
  <div class="overflow-hidden rounded-md bg-white">
    <div class="overflow-x-auto">
      <table class="w-full border-collapse">
        <thead>
          <tr class="bg-gray-50">
            <th class="text-center py-3 px-2 font-normal text-xs text-gray-400 border border-gray-200 w-12">
              #
            </th>
            <th class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200">
              Adventurer
            </th>
            <th
              v-if="showBatchColumn"
              class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
            >
              Batch
            </th>
            <th class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200">
              Tuition Option
            </th>
            <th class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200">Program Status</th>
            <th class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200">Relationship</th>
            <th class="text-center py-3 font-normal text-xs text-gray-700 border border-gray-200 max-w-16">Expand</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(adventurer, index) in localAdventurers"
            :key="adventurer.id"
            class="hover:bg-gray-50 "
          >
            <!-- Index Column -->
            <td class="py-1 px-2 border border-gray-100 text-center text-xs text-gray-340 w-12">
              {{ index + 1 }}
            </td>
            <!-- Adventurer Info Column -->
            <td class="py-1 px-2 border border-gray-100 flex items-center gap-2">
              <FellowAvatarDropdown
                :user-id="adventurer.profile_details.user_details.id"
                :username="adventurer.profile_details.user_details.username"
                :avatar-src="adventurer.profile_details?.user_details?.avatar_image"
                :name="`${adventurer.profile_details?.user_details?.first_name} ${adventurer.profile_details?.user_details?.last_name}`"
                :curriculum-path="`/manage/campus/fellows/${adventurer.profile_details.user_details.id}`"
                :is-blurred="isBlurred"
              />

                <div class="flex-1">
                  <h3 class="text-sm font-medium flex items-center gap-2" :class="{ 'blur-md select-none': isBlurred }">
                    {{ adventurer.profile_details?.user_details?.first_name }}
                    {{ adventurer.profile_details?.user_details?.last_name }}
                    {{
                      getCountryFlag(
                        adventurer.country_of_residence?.country_name ??
                          adventurer.profile_details?.country_of_residence_details?.country_name
                      )
                    }}
                    <div v-if="!isBlurred" class="flex gap-2">
                      <a
                        v-if="adventurer.profile_details?.linkedin_url"
                        :href="adventurer.profile_details.linkedin_url"
                        target="_blank"
                        class="hover:opacity-75"
                        @click.stop
                      >
                        <img src="/linkedin_logo.svg" class="w-4 h-4" alt="LinkedIn" />
                      </a>
                      <a
                        v-if="adventurer.profile_details?.telegram_handle"
                        :href="`https://t.me/${adventurer.profile_details.telegram_handle}`"
                        target="_blank"
                        class="hover:opacity-75"
                        @click.stop
                      >
                        <img src="/telegram_logo.svg" class="w-4 h-4" alt="Telegram" />
                      </a>
                    </div>
                  </h3>
                  <p class="text-xs text-gray-500" :class="{ 'blur-md select-none': isBlurred }">
                    {{ adventurer.profile_details?.user_details?.email }}
                  </p>
                </div>
            </td>

            <td v-if="showBatchColumn" class="py-1 px-2 border border-gray-100 text-xs">
              <span
                v-if="adventurer.batch"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getBatchTagClass(adventurer.batch)"
              >
                {{ formatBatchName(adventurer.batch) }}
              </span>
            </td>

            <td class="py-1 px-2 border border-gray-100 text-xs">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getTuitionOptionClassFromAdventurer(adventurer)"
              >
                {{ getTuitionOptionLabelFromAdventurer(adventurer) }}
              </span>
            </td>

            <!-- Program Status Column -->
            <td class="py-1 px-2 border border-gray-100 text-xs">
              <Select
                v-model="adventurer.program_status"
                @update:model-value="(value) => handleProgramStatusChange(value, adventurer)"
                :disabled="isUpdatingProgramStatus[adventurer.id]"
              >
                <SelectTrigger class="w-full max-w-[12.5rem]">
                  <SelectValue>
                    <template
                      v-if="
                        adventurer.program_status &&
                        programStatusChoices[
                          adventurer.program_status as keyof typeof programStatusChoices
                        ]
                      "
                    >
                      <div
                        :class="
                          getProgramStatusClass(
                            programStatusChoices[
                              adventurer.program_status as keyof typeof programStatusChoices
                            ]
                          )
                        "
                      >
                        {{
                          programStatusChoices[
                            adventurer.program_status as keyof typeof programStatusChoices
                          ]
                        }}
                        <Loader2
                          v-if="isUpdatingProgramStatus[adventurer.id]"
                          class="animate-spin h-4 w-4 inline ml-1"
                        />
                      </div>
                    </template>
                    <span v-else class="text-gray-400">Select status...</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="(value, key) in programStatusChoices"
                      :key="key"
                      :value="key"
                    >
                      <div :class="getProgramStatusClass(value)">
                        {{ value }}
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </td>

            <!-- Relationship Status Column -->
            <td class="py-1 px-2 border border-gray-100">
              <Select
                v-model="adventurer.relationship_status"
                @update:model-value="(value) => handleRelationshipChange(value, adventurer)"
                :disabled="isUpdatingRelationship[adventurer.id]"
              >
                <SelectTrigger class="w-full max-w-[12.5rem]">
                  <SelectValue>
                    <template
                      v-if="
                        adventurer.relationship_status &&
                        relationshipChoices[
                          adventurer.relationship_status as keyof typeof relationshipChoices
                        ]
                      "
                    >
                      <div
                        :class="
                          getRelationshipStatusClass(
                            relationshipChoices[
                              adventurer.relationship_status as keyof typeof relationshipChoices
                            ]
                          )
                        "
                      >
                        {{
                          relationshipChoices[
                            adventurer.relationship_status as keyof typeof relationshipChoices
                          ]
                        }}
                        <Loader2
                          v-if="isUpdatingRelationship[adventurer.id]"
                          class="animate-spin h-4 w-4 inline ml-1"
                        />
                      </div>
                    </template>
                    <span v-else class="text-gray-400">Select status...</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem v-for="(value, key) in relationshipChoices" :key="key" :value="key">
                      <div :class="getRelationshipStatusClass(value)">
                        {{ value }}
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </td>

            <!-- Actions Column -->
            <td class="py-1 px-2 border border-gray-100 w-16">
              <div class="flex items-center justify-center w-full">
                <button
                  @click="openDetails(adventurer)"
                  class="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-1.5"
                >
                  <Expand class="w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Fellow Details Slide-over -->
    <BatchFellowExpanded
      v-if="selectedAdventurer"
      :selected-adventurer="selectedAdventurer"
      :is-updating-program-status="!!isUpdatingProgramStatus[selectedAdventurer.id]"
      :is-updating-relationship="!!isUpdatingRelationship[selectedAdventurer.id]"
      @close="closeDetails"
      @program-status-change="
        (value) => selectedAdventurer && handleProgramStatusChange(value, selectedAdventurer)
      "
      @relationship-change="
        (value) => selectedAdventurer && handleRelationshipChange(value, selectedAdventurer)
      "
      @comments-updated="(comments) => updateComments(comments)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { Expand, Loader2 } from 'lucide-vue-next'
import FellowAvatarDropdown from '@/components/common/FellowAvatarDropdown.vue'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { programStatusChoices, relationshipChoices } from '@/const/fellowChoices'
import { getProgramStatusClass, getRelationshipStatusClass } from '@/utils/statusClasses'
import {
  handleProgramStatusChange as updateProgramStatus,
  handleRelationshipChange as updateRelationship,
  type FellowDataUpdateCallbacks
} from '@/utils/fellowDataManagement'
import { toast } from '@/components/ui/toast'
import BatchFellowExpanded from './BatchFellowExpanded.vue'
import type { Adventurer } from '@/types/adventurer'
import { formatBatchName } from '@/utils/formatBatchName'
import { getCountryFlag } from '@/utils/countryFlags'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const { isBlurred } = usePrivacyMode()

const props = withDefaults(defineProps<{
  batchName: string
  adventurers: Adventurer[]
  clickableHeader?: boolean
  showBatchColumn?: boolean
}>(), {
  showBatchColumn: false
})

defineEmits<{
  (e: 'header-click'): void
}>()

// Create a local reactive copy of adventurers
const localAdventurers = ref<Adventurer[]>([])

const selectedAdventurer = ref<Adventurer | null>(null)
const isUpdatingProgramStatus = ref<Record<string, boolean>>({})
const isUpdatingRelationship = ref<Record<string, boolean>>({})

const batchTagClasses = [
  'bg-blue-50 text-blue-700',
  'bg-emerald-50 text-emerald-700',
  'bg-amber-50 text-amber-700',
  'bg-violet-50 text-violet-700',
  'bg-sky-50 text-sky-700',
  'bg-rose-50 text-rose-700',
  'bg-lime-50 text-lime-700'
]

const tuitionOptionLabels: Record<string, string> = {
  PLAN_DEFERRED: 'Deferred: $250/mo + 10% ISA',
  PLAN_BALANCED: 'Balanced: $900/mo + 5% ISA',
  PLAN_FULL: 'Full Tuition: $2000/mo, no ISA',
  PLAN_LEGACY: 'Legacy Pricing'
}

const tuitionOptionClasses: Record<string, string> = {
  PLAN_DEFERRED: 'bg-amber-50 text-amber-700',
  PLAN_BALANCED: 'bg-blue-50 text-blue-700',
  PLAN_FULL: 'bg-emerald-50 text-emerald-700',
  PLAN_LEGACY: 'bg-slate-50 text-slate-700'
}

const getBatchTagClass = (batchId: string) => {
  let hash = 0
  for (let i = 0; i < batchId.length; i += 1) {
    hash = (hash * 31 + batchId.charCodeAt(i)) % batchTagClasses.length
  }
  return batchTagClasses[Math.abs(hash) % batchTagClasses.length]
}

const getTuitionOptionLabel = (tuitionOption?: string | null) => {
  if (!tuitionOption) return '—'
  return tuitionOptionLabels[tuitionOption] || tuitionOption
}

const getTuitionOptionClass = (tuitionOption?: string | null) => {
  if (!tuitionOption) return 'bg-gray-50 text-gray-600'
  return tuitionOptionClasses[tuitionOption] || 'bg-gray-50 text-gray-600'
}

const getTuitionOptionLabelFromAdventurer = (adventurer: Adventurer) => {
  return getTuitionOptionLabel((adventurer as Adventurer & { tuition_option?: string | null }).tuition_option)
}

const getTuitionOptionClassFromAdventurer = (adventurer: Adventurer) => {
  return getTuitionOptionClass((adventurer as Adventurer & { tuition_option?: string | null }).tuition_option)
}

// Initialize program and relationship status from existing data
onMounted(() => {
  // Initialize local copy
  localAdventurers.value = props.adventurers.map(adventurer => {
    const updatedAdventurer = { ...adventurer }
    
    // Initialize program status - API sends us the key directly
    if (adventurer.outtalent_program_status) {
      updatedAdventurer.program_status = adventurer.outtalent_program_status as keyof typeof programStatusChoices
    }

    // Initialize relationship status - API sends us the key directly
    if (adventurer.outtalent_relationship) {
      updatedAdventurer.relationship_status = adventurer.outtalent_relationship as keyof typeof relationshipChoices
    }

    // Ensure batch information is set
    if (!updatedAdventurer.batch) {
      updatedAdventurer.batch = props.batchName
    }
    
    return updatedAdventurer
  })
})

const handleProgramStatusChange = async (value: unknown, adventurer: Adventurer) => {
  if (
    !adventurer.profile_details?.user_details?.username ||
    isUpdatingProgramStatus.value[adventurer.id]
  )
    return

  const firstName = adventurer.profile_details?.user_details?.first_name || 'Adventurer'
  const key = value as keyof typeof programStatusChoices

  isUpdatingProgramStatus.value[adventurer.id] = true
  const callbacks: FellowDataUpdateCallbacks = {
    onSuccess: () => {
      adventurer.program_status = key
      // Update in local array
      const index = localAdventurers.value.findIndex(a => a.id === adventurer.id)
      if (index !== -1) {
        localAdventurers.value[index] = { ...adventurer }
      }
      toast({
        title: 'Program Status Updated',
        description: `${firstName}'s program status has been updated to ${programStatusChoices[key]}`
      })
    },
    onError: () => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update program status. Please try again.',
        variant: 'destructive'
      })
    },
    onFinally: () => {
      isUpdatingProgramStatus.value[adventurer.id] = false
    }
  }
  await updateProgramStatus(
    adventurer.profile_details.user_details.username,
    value,
    adventurer,
    callbacks
  )
}

const handleRelationshipChange = async (value: unknown, adventurer: Adventurer) => {
  if (
    !adventurer.profile_details?.user_details?.username ||
    isUpdatingRelationship.value[adventurer.id]
  )
    return

  const firstName = adventurer.profile_details?.user_details?.first_name || 'Adventurer'
  const key = value as keyof typeof relationshipChoices

  isUpdatingRelationship.value[adventurer.id] = true
  const callbacks: FellowDataUpdateCallbacks = {
    onSuccess: () => {
      adventurer.relationship_status = key
      // Update in local array
      const index = localAdventurers.value.findIndex(a => a.id === adventurer.id)
      if (index !== -1) {
        localAdventurers.value[index] = { ...adventurer }
      }
      toast({
        title: 'Relationship Status Updated',
        description: `${firstName}'s relationship status has been updated to ${relationshipChoices[key]}`
      })
    },
    onError: () => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update relationship status. Please try again.',
        variant: 'destructive'
      })
    },
    onFinally: () => {
      isUpdatingRelationship.value[adventurer.id] = false
    }
  }
  await updateRelationship(
    adventurer.profile_details.user_details.username,
    value,
    adventurer,
    callbacks
  )
}

// Watch for changes in props.adventurers
watch(() => props.adventurers, (newAdventurers) => {
  localAdventurers.value = newAdventurers.map(adventurer => {
    const updatedAdventurer = { ...adventurer }
    if (adventurer.outtalent_program_status) {
      updatedAdventurer.program_status = adventurer.outtalent_program_status as keyof typeof programStatusChoices
    }
    if (adventurer.outtalent_relationship) {
      updatedAdventurer.relationship_status = adventurer.outtalent_relationship as keyof typeof relationshipChoices
    }
    // Ensure batch information is set
    if (!updatedAdventurer.batch) {
      updatedAdventurer.batch = props.batchName
    }
    return updatedAdventurer
  })
}, { deep: true })

const updateComments = (comments: any[]) => {
  if (selectedAdventurer.value) {
    selectedAdventurer.value.internal_comments = comments
  }
}

const openDetails = (adventurer: Adventurer) => {
  selectedAdventurer.value = adventurer
}

const closeDetails = () => {
  selectedAdventurer.value = null
}

// Add console log to debug batch names
watch(() => localAdventurers.value, (newValue) => {
  console.log('Adventurers updated:', newValue.map(a => ({ id: a.id, batch: a.batch })))
}, { deep: true })
</script>
